// frontend/components/settings/ProfileChangeRequestModal.tsx

"use client";

import { useState, useEffect } from "react";
import { X, Send, CheckCircle } from "lucide-react";
import { API_URL, authHeaders } from "../../utils/api";
import { ALL_DEPARTMENTS } from "../../constants/organization";

const POSITIONS = ["사원", "주임", "대리", "과장", "차장", "부장", "이사", "상무", "전무", "대표이사"];

interface ProfileChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: {
    department?: string;
    position?: string;
    isDeptHead?: boolean;
  };
  accessToken: string;
}

export default function ProfileChangeRequestModal({ isOpen, onClose, currentUser, accessToken }: ProfileChangeRequestModalProps) {
  const [department, setDepartment] = useState(currentUser.department || "");
  const [position, setPosition]     = useState(currentUser.position || "");
  const [isDeptHead, setIsDeptHead] = useState(currentUser.isDeptHead || false);
  const [loading, setLoading]       = useState(false);
  const [sent, setSent]             = useState(false);
  const [error, setError]           = useState("");

  useEffect(() => {
    if (isOpen) {
      setDepartment(currentUser.department || "");
      setPosition(currentUser.position || "");
      setIsDeptHead(currentUser.isDeptHead || false);
      setSent(false);
      setError("");
    }
  }, [isOpen, currentUser.department, currentUser.position, currentUser.isDeptHead]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/system/request/profile-change`, {
        method: "POST",
        headers: authHeaders(accessToken),
        body: JSON.stringify({ department, position, isDeptHead }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "요청 전송에 실패했습니다.");
      }
      setSent(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const inputCls = "w-full px-3 py-2 text-sm bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-700 rounded-lg outline-none focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500 text-gray-800 dark:text-zinc-100";
  const labelCls = "block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-zinc-900 w-80 rounded-xl shadow-2xl border border-gray-200 dark:border-zinc-800 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100 dark:border-zinc-800">
          <p className="text-sm font-bold text-gray-800 dark:text-zinc-100">직급·소속 변경 요청</p>
          <button onClick={onClose} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="p-4">
          {sent ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle size={36} className="text-green-500" />
              <p className="text-sm font-medium text-gray-700 dark:text-zinc-200">요청이 전송되었습니다.</p>
              <p className="text-xs text-gray-400 dark:text-zinc-500">관리자 검토 후 처리됩니다.</p>
              <button onClick={onClose} className="mt-2 px-4 py-1.5 text-xs bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors">
                닫기
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <p className="text-xs text-gray-400 dark:text-zinc-500 mb-3">
                변경하고 싶은 소속·직책·부서장 여부를 설정한 후 요청을 보내세요.
              </p>

              <div>
                <label className={labelCls}>소속</label>
                <select value={department} onChange={e => setDepartment(e.target.value)} className={inputCls}>
                  <option value="">선택 안 함</option>
                  {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div>
                <label className={labelCls}>직책</label>
                <div className="flex items-center gap-3">
                  <select value={position} onChange={e => setPosition(e.target.value)} className={`${inputCls} flex-1`}>
                    <option value="">선택 안 함</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-zinc-300 whitespace-nowrap cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isDeptHead}
                      onChange={e => setIsDeptHead(e.target.checked)}
                      className="w-3.5 h-3.5 accent-blue-500"
                    />
                    부서장
                  </label>
                </div>
              </div>

              {error && <p className="text-xs text-red-500">{error}</p>}

              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#006DBA] hover:bg-[#005A9E] disabled:opacity-60 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <Send size={12} />
                  {loading ? "전송 중…" : "변경 요청 보내기"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-2 border border-gray-200 dark:border-zinc-700 text-xs text-gray-600 dark:text-zinc-400 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  취소
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
